import { useCallback, useEffect, useState } from "react";
import { useSearchParams, type NavigateOptions } from "react-router-dom";
import { useDebounce } from "@shared/hookHelpers";

export type ParamValue = string | number | null | undefined;

/**
 * URL-backed `page` / `search` state shared by the paginated template lists.
 * The search box is debounced into the URL and kept in sync with back/forward navigation.
 */
const useTemplateSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const urlSearch = searchParams.get("search") || "";

  const [search, setSearch] = useState(urlSearch);
  const debouncedSearch = useDebounce(search, 500) as string;

  const updateParams = useCallback(
    (updates: Record<string, ParamValue>, options?: NavigateOptions) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === undefined || value === "") next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      }, options),
    [setSearchParams],
  );

  useEffect(() => {
    setSearch(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    if (debouncedSearch === urlSearch) return;
    updateParams({ search: debouncedSearch, page: null }, { replace: true });
  }, [debouncedSearch]);

  return { searchParams, page, urlSearch, search, setSearch, updateParams };
};

export default useTemplateSearchParams;
